import React, { useState } from 'react';
import { Editor } from '@tiptap/react';
import * as Y from 'yjs';
import { exportToWord, exportToPDF, exportToEpub } from '../utils/export-utils';
import type {
  ExportFontId,
  ExportFontSizePt,
  ExportLayoutOptions,
  LineSpacing,
  MarginsCm,
  PageNumberPosition,
} from '../utils/export-utils';
import { EXPORT_FONT_SIZE_PTS } from '../utils/export-utils';
import { API_BASE } from '../config';
import { ClosePanelButton } from './ClosePanelButton';
import { Button } from './ui/button';

interface ExportDocumentProps {
  editor: Editor | null;
  ydoc?: Y.Doc | null;
  documentTitle: string;
  projectTitle?: string;
  documentId: string | undefined;
  token: string | null;
  comments: Array<{
    id: string;
    content: string;
    selectedText?: string;
    userEmail?: string;
    replies?: Array<{
      id: string;
      comment: string;
      userEmail?: string;
      createdAt?: string;
    }>;
  }>;
  suggestions: Array<{
    id: string;
    userId: string;
    userEmail?: string;
    documentId: string | null;
    originalText: string;
    suggestionText: string;
    issueType: 'orthographic' | 'preciseness' | 'clarity' | 'consistency' | 'orthographic_consistency' | 'grammar' | 'spelling' | 'punctuation' | 'sentence_structure' | 'repetition' | 'overused_words' | 'cacophony' | 'passive_voice' | 'show_vs_tell' | 'pacing' | 'adverbs' | 'adjectives' | 'cliches' | 'transitions' | 'style' | 'strength' | 'user_edit';
    feedbackText: string;
    llmCallId: string | null;
    llmCall?: {
      id: string;
      userId: string | null;
      documentId: string | null;
      triggerSource: string;
      provider: 'gemini' | 'openai';
      model: string;
      prompt: string;
      response: string | null;
      promptTokens: number | null;
      responseTokens: number | null;
      costUsd: number | null;
      createdAt: string;
    } | null;
    languageCode: string | null;
    languageName: string | null;
    shownAt: string;
    status: 'pending' | 'applied' | 'rejected' | 'reported';
    closedAt: string | null;
    createdAt: string;
  }>;
  t: (key: string) => string;
  onClose?: () => void;
  hideHeading?: boolean;
}

interface WordMarginsInput {
  top: string;
  right: string;
  bottom: string;
  left: string;
}

export const ExportDocument: React.FC<ExportDocumentProps> = ({
  editor,
  ydoc,
  documentTitle,
  projectTitle,
  documentId,
  token,
  comments,
  suggestions,
  t,
  onClose,
  hideHeading = false,
}) => {
  const [exportComments, setExportComments] = useState<boolean>(false);
  const [exportSuggestions, setExportSuggestions] = useState<boolean>(false);
  const [includeCoverPage, setIncludeCoverPage] = useState<boolean>(false);
  const [coverAuthorName, setCoverAuthorName] = useState<string>('');
  const [pageNumberPosition, setPageNumberPosition] = useState<PageNumberPosition>('bottom_right');
  const [lineSpacing, setLineSpacing] = useState<LineSpacing>(1.5);
  const [marginsInput, setMarginsInput] = useState<WordMarginsInput>({
    top: '2.5',
    right: '2.5',
    bottom: '2.5',
    left: '2.5',
  });
  const [exportFontId, setExportFontId] = useState<'' | ExportFontId>('');
  const [exportFontSizePt, setExportFontSizePt] = useState<'' | ExportFontSizePt>('');

  const getMarginsCm = (): MarginsCm => {
    const fallback = 2.5;
    const parseCm = (value: string): number => {
      const parsed = Number.parseFloat(value);
      if (!Number.isFinite(parsed) || Number.isNaN(parsed) || parsed < 0) return fallback;
      return parsed;
    };

    return {
      top: parseCm(marginsInput.top),
      right: parseCm(marginsInput.right),
      bottom: parseCm(marginsInput.bottom),
      left: parseCm(marginsInput.left),
    };
  };

  const buildExportLayoutOptions = (): ExportLayoutOptions => ({
    pageNumberPosition,
    lineSpacing,
    marginsCm: getMarginsCm(),
    ...(exportFontId ? { exportFontId } : {}),
    ...(exportFontSizePt ? { exportFontSizePt } : {}),
  });

  const handleExportToWord = async (): Promise<void> => {
    const exportLayoutOptions = buildExportLayoutOptions();

    await exportToWord({
      editor,
      ydoc,
      documentTitle,
      projectTitle,
      documentId,
      token,
      apiBase: API_BASE,
      exportComments,
      exportSuggestions,
      includeCoverPage,
      coverAuthorName: includeCoverPage ? coverAuthorName.trim() : undefined,
      comments,
      suggestions,
      t,
      exportLayoutOptions,
    });
  };

  const handleExportToPDF = async (): Promise<void> => {
    const exportLayoutOptions = buildExportLayoutOptions();

    await exportToPDF({
      editor,
      ydoc,
      documentTitle,
      projectTitle,
      documentId,
      token,
      apiBase: API_BASE,
      exportComments,
      exportSuggestions,
      includeCoverPage,
      coverAuthorName: includeCoverPage ? coverAuthorName.trim() : undefined,
      comments,
      suggestions,
      t,
      exportLayoutOptions,
    });
  };

  const handleExportToEpub = async (): Promise<void> => {
    await exportToEpub({
      editor,
      ydoc,
      documentTitle,
      projectTitle,
      documentId,
      token,
      apiBase: API_BASE,
      exportComments,
      exportSuggestions,
      comments,
      suggestions,
      t,
      exportLayoutOptions: buildExportLayoutOptions(),
    });
  };

  const fieldsetStyle: React.CSSProperties = {
    border: '1px solid var(--border)',
    borderRadius: '0.5rem',
    padding: '1rem',
    marginBottom: '1rem',
    backgroundColor: 'var(--card)',
  };

  const legendStyle: React.CSSProperties = {
    padding: '0 0.5rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: 'var(--foreground)',
  };

  return (
    <div>
      {hideHeading ? null : (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600, color: 'var(--foreground)' }}>{t('export')}</h2>
          {onClose && (
            <ClosePanelButton onClick={onClose} ariaLabel={t('close')} />
          )}
        </div>
      )}
      <div style={{ padding: '1.5rem', borderRadius: '0.5rem', backgroundColor: 'var(--card)' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '0.75rem',
            marginBottom: '1rem',
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.875rem', color: 'var(--foreground)' }}>
            <span>{t('export_word_page_number')}</span>
            <select
              value={pageNumberPosition}
              onChange={(e) => setPageNumberPosition(e.target.value as PageNumberPosition)}
              disabled={!editor}
              style={{
                padding: '0.5rem 0.75rem',
                border: '1px solid var(--border)',
                borderRadius: '0.5rem',
                backgroundColor: 'var(--background)',
                color: 'var(--foreground)',
              }}
            >
              <option value="none">{t('export_word_page_number_none')}</option>
              <option value="bottom_left">{t('export_word_page_number_bottom_left')}</option>
              <option value="bottom_middle">{t('export_word_page_number_bottom_middle')}</option>
              <option value="bottom_right">{t('export_word_page_number_bottom_right')}</option>
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.875rem', color: 'var(--foreground)' }}>
            <span>{t('export_word_line_spacing')}</span>
            <select
              value={lineSpacing}
              onChange={(e) => setLineSpacing((e.target.value === 'default' ? 'default' : Number(e.target.value)) as LineSpacing)}
              disabled={!editor}
              style={{
                padding: '0.5rem 0.75rem',
                border: '1px solid var(--border)',
                borderRadius: '0.5rem',
                backgroundColor: 'var(--background)',
                color: 'var(--foreground)',
              }}
            >
              <option value="default">{t('export_word_line_spacing_default')}</option>
              <option value="1">1</option>
              <option value="1.25">1.25</option>
              <option value="1.5">1.5</option>
              <option value="2">2</option>
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.875rem', color: 'var(--foreground)' }}>
            <span>{t('export_font')}</span>
            <select
              value={exportFontId}
              onChange={(e) => setExportFontId((e.target.value === '' ? '' : e.target.value) as '' | ExportFontId)}
              disabled={!editor}
              style={{
                padding: '0.5rem 0.75rem',
                border: '1px solid var(--border)',
                borderRadius: '0.5rem',
                backgroundColor: 'var(--background)',
                color: 'var(--foreground)',
              }}
            >
              <option value="">{t('export_font_default')}</option>
              <optgroup label={t('export_font_serif')}>
                <option value="times_new_roman">{t('export_font_times_new_roman')}</option>
                <option value="georgia">{t('export_font_georgia')}</option>
                <option value="garamond">{t('export_font_garamond')}</option>
              </optgroup>
              <optgroup label={t('export_font_sans_serif')}>
                <option value="arial">{t('export_font_arial')}</option>
                <option value="helvetica">{t('export_font_helvetica')}</option>
                <option value="verdana">{t('export_font_verdana')}</option>
              </optgroup>
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.875rem', color: 'var(--foreground)' }}>
            <span>{t('export_font_size')}</span>
            <select
              value={exportFontSizePt}
              onChange={(e) => {
                const value = e.target.value;
                setExportFontSizePt(value === '' ? '' : Number(value) as ExportFontSizePt);
              }}
              disabled={!editor}
              style={{
                padding: '0.5rem 0.75rem',
                border: '1px solid var(--border)',
                borderRadius: '0.5rem',
                backgroundColor: 'var(--background)',
                color: 'var(--foreground)',
              }}
            >
              <option value="">{t('export_font_size_default')}</option>
              {EXPORT_FONT_SIZE_PTS.map((sizePt) => (
                <option key={sizePt} value={sizePt}>
                  {sizePt}
                </option>
              ))}
            </select>
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', gridColumn: '1 / -1' }}>
            <span style={{ fontSize: '0.875rem', color: 'var(--foreground)' }}>{t('export_word_margins')}</span>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.5rem' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.75rem', color: 'var(--muted-foreground)' }}>
                <span>{t('export_word_margin_top')}</span>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  min="0"
                  value={marginsInput.top}
                  onChange={(e) => setMarginsInput((prev) => ({ ...prev, top: e.target.value }))}
                  disabled={!editor}
                  style={{
                    padding: '0.375rem 0.5rem',
                    border: '1px solid var(--border)',
                    borderRadius: '0.5rem',
                    backgroundColor: 'var(--background)',
                    color: 'var(--foreground)',
                    fontSize: '0.75rem',
                    height: '32px',
                  }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.75rem', color: 'var(--muted-foreground)' }}>
                <span>{t('export_word_margin_bottom')}</span>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  min="0"
                  value={marginsInput.bottom}
                  onChange={(e) => setMarginsInput((prev) => ({ ...prev, bottom: e.target.value }))}
                  disabled={!editor}
                  style={{
                    padding: '0.375rem 0.5rem',
                    border: '1px solid var(--border)',
                    borderRadius: '0.5rem',
                    backgroundColor: 'var(--background)',
                    color: 'var(--foreground)',
                    fontSize: '0.75rem',
                    height: '32px',
                  }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.75rem', color: 'var(--muted-foreground)' }}>
                <span>{t('export_word_margin_left')}</span>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  min="0"
                  value={marginsInput.left}
                  onChange={(e) => setMarginsInput((prev) => ({ ...prev, left: e.target.value }))}
                  disabled={!editor}
                  style={{
                    padding: '0.375rem 0.5rem',
                    border: '1px solid var(--border)',
                    borderRadius: '0.5rem',
                    backgroundColor: 'var(--background)',
                    color: 'var(--foreground)',
                    fontSize: '0.75rem',
                    height: '32px',
                  }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.75rem', color: 'var(--muted-foreground)' }}>
                <span>{t('export_word_margin_right')}</span>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  min="0"
                  value={marginsInput.right}
                  onChange={(e) => setMarginsInput((prev) => ({ ...prev, right: e.target.value }))}
                  disabled={!editor}
                  style={{
                    padding: '0.375rem 0.5rem',
                    border: '1px solid var(--border)',
                    borderRadius: '0.5rem',
                    backgroundColor: 'var(--background)',
                    color: 'var(--foreground)',
                    fontSize: '0.75rem',
                    height: '32px',
                  }}
                />
              </label>
            </div>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
            gap: '1rem',
            marginBottom: '0.75rem',
          }}
        >
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={includeCoverPage}
              onChange={(e) => setIncludeCoverPage(e.target.checked)}
              style={{
                width: '1rem',
                height: '1rem',
                cursor: 'pointer',
              }}
            />
            <span style={{ fontSize: '0.875rem', color: 'var(--foreground)' }}>{t('export_cover')}</span>
          </label>
          <label
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.25rem',
              flex: '1',
              minWidth: 'min(100%, 220px)',
              fontSize: '0.875rem',
              color: 'var(--foreground)',
            }}
          >
            <span>{t('export_cover_author')}</span>
            <input
              type="text"
              value={coverAuthorName}
              onChange={(e) => setCoverAuthorName(e.target.value)}
              disabled={!editor || !includeCoverPage}
              autoComplete="off"
              style={{
                padding: '0.5rem 0.75rem',
                border: '1px solid var(--border)',
                borderRadius: '0.5rem',
                backgroundColor: 'var(--background)',
                color: 'var(--foreground)',
                opacity: includeCoverPage ? 1 : 0.6,
              }}
            />
          </label>
        </div>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            cursor: 'pointer',
            marginBottom: '0.75rem',
          }}
        >
          <input
            type="checkbox"
            checked={exportComments}
            onChange={(e) => setExportComments(e.target.checked)}
            style={{
              width: '1rem',
              height: '1rem',
              cursor: 'pointer',
            }}
          />
          <span style={{ fontSize: '0.875rem', color: 'var(--foreground)' }}>{t('export_comments')}</span>
        </label>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            cursor: 'pointer',
            marginBottom: '1rem',
          }}
        >
          <input
            type="checkbox"
            checked={exportSuggestions}
            onChange={(e) => setExportSuggestions(e.target.checked)}
            style={{
              width: '1rem',
              height: '1rem',
              cursor: 'pointer',
            }}
          />
          <span style={{ fontSize: '0.875rem', color: 'var(--foreground)' }}>{t('export_suggestions')}</span>
        </label>
        <fieldset style={fieldsetStyle}>
          <legend style={legendStyle}>{t('export_to_word')}</legend>
          <Button
            type="button"
            onClick={handleExportToWord}
            disabled={!editor}
            className="w-full"
          >
            {t('export_to_word')}
          </Button>
        </fieldset>
        <fieldset style={fieldsetStyle}>
          <legend style={legendStyle}>{t('export_to_pdf')}</legend>
          <Button
            type="button"
            onClick={handleExportToPDF}
            disabled={!editor}
            className="w-full"
          >
            {t('export_to_pdf')}
          </Button>
        </fieldset>
        <fieldset style={fieldsetStyle}>
          <legend style={legendStyle}>{t('export_to_epub')}</legend>
          <Button
            type="button"
            onClick={handleExportToEpub}
            disabled={!editor}
            className="w-full"
          >
            {t('export_to_epub')}
          </Button>
        </fieldset>
      </div>
    </div>
  );
};

